import React, {
  createContext, FC, useCallback, useContext, useEffect,
} from 'react';
import { useAuth0, RedirectLoginOptions } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { IdleTimerProvider } from 'react-idle-timer';
import { isAxiosError } from 'axios';
import { decodeJwt } from '../../utils/jwt';
import { JWT_LOCAL_STORAGE_KEY } from '../../constants/constants';
import { environment } from '../../environments/environment';
import { ParentProps } from '../../models';
import { usePatientContext } from '../patient-context';
import localStorage from '../../services/localStorage';

import { AuthAccessTokenDecoded, AuthContextValues } from './model';
import { useLoadingSpinnerContext } from '../loading-spinner-context';
import logoffSession from '../../services/auth/logoff';
import { clearHttpErrorInterceptor, setHttpErrorInterceptor } from '../../services/httpclient';

export const AuthContext = createContext<AuthContextValues>({} as AuthContextValues);

/** For readability: Minutes * 60 * 1000 */
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * The goal of this provider is to abstract the Auth0 layer
 * that is defined in an upper level.
 */
interface Auth0ProviderProps extends ParentProps {
  idlenessTimeout?: number;
}

export const AuthProvider: FC<Auth0ProviderProps> = (props) => {
  const {
    children,
    idlenessTimeout = IDLE_TIMEOUT_MS,
  } = props;

  const [searchParams] = useSearchParams();

  const httpResponseInterceptorIdRef = React.useRef<number | null>(null);

  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  useEffect(() => {
    if (!environment.disableAuth0 && isAuthenticated) {
      httpResponseInterceptorIdRef.current = setHttpErrorInterceptor((error: Error) => {
        if (isAxiosError(error) && error.response?.status === 401) {
          localStorage.remove(JWT_LOCAL_STORAGE_KEY);
          logout({ returnTo: window.location.origin });
        }
      });
    } else if (httpResponseInterceptorIdRef.current !== null) {
      clearHttpErrorInterceptor(httpResponseInterceptorIdRef.current);
      httpResponseInterceptorIdRef.current = null;
    }

    return () => {
      if (httpResponseInterceptorIdRef.current !== null) {
        clearHttpErrorInterceptor(httpResponseInterceptorIdRef.current);
        httpResponseInterceptorIdRef.current = null;
      }
    };
  }, [isAuthenticated, logout]);

  const {
    setShowingLoadingSpinner,
  } = useLoadingSpinnerContext();

  const { getPatient } = usePatientContext();

  const getValidStoredToken = (): { token: string; decoded: AuthAccessTokenDecoded } | null => {
    const storedToken = localStorage.get(JWT_LOCAL_STORAGE_KEY);

    if (!storedToken) {
      return null;
    }

    const decodedToken = decodeJwt<AuthAccessTokenDecoded>(storedToken);

    if (!decodedToken) {
      localStorage.remove(JWT_LOCAL_STORAGE_KEY);
      return null;
    }

    const currentTime = Date.now() / 1000; // in seconds
    if (decodedToken.exp < currentTime) {
      localStorage.remove(JWT_LOCAL_STORAGE_KEY);
      return null;
    }

    return {
      token: storedToken,
      decoded: decodedToken,
    };
  };

  const logoutHandler = useCallback(async () => {
    try {
      setShowingLoadingSpinner(
        'auth',
        true,
      );
      await logoffSession();
      localStorage.remove(JWT_LOCAL_STORAGE_KEY);
      logout({ returnTo: window.location.origin });
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error(e);
      setShowingLoadingSpinner(
        'auth',
        false,
      );
    }
  }, []);
  
  const getUserMetadata = async () => {
    try {
      let accessTokenDecoded: AuthAccessTokenDecoded | null = null;
      const storedTokenData = getValidStoredToken();

      if (storedTokenData) {
        accessTokenDecoded = storedTokenData.decoded;
      } else {
        const accessToken = await getAccessTokenSilently({
          audience: environment.auth0Audience,
          ignoreCache: false,
        });

        localStorage.set(JWT_LOCAL_STORAGE_KEY, accessToken);
        accessTokenDecoded = decodeJwt<AuthAccessTokenDecoded>(accessToken);
      }

      if (accessTokenDecoded) {
        const patientId = accessTokenDecoded['https://studykik.com/studykik_id'];
        
        await getPatient(patientId);
      }
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  useEffect(() => {
    if (!environment.disableAuth0 && !isLoading && !isAuthenticated) {
      const loginParams: RedirectLoginOptions = {
        redirectUri: window.location.origin,
        audience: environment.auth0Audience,
      };

      const invitationFlag = searchParams.get('invitation');
      const organizationFlag = searchParams.get('organization');

      if (invitationFlag !== null) {
        loginParams.invitation = invitationFlag;
      }
      if (organizationFlag !== null) {
        loginParams.organization = organizationFlag;
      }

      loginWithRedirect(loginParams);
    } else if (isAuthenticated) {
      getUserMetadata();
    } else if (environment.disableAuth0 === true) {
      getPatient('test');
    }
  }, [isLoading, isAuthenticated, searchParams]);

  useEffect(() => {
    setShowingLoadingSpinner(
      'auth',
      !environment.disableAuth0 && (isLoading || !isAuthenticated),
    );
  }, [isLoading, isAuthenticated]);

  const value: AuthContextValues = {
    isLogged: isAuthenticated,
    isLoading,
    logout: logoutHandler,
    user,
  };

  return (
    <AuthContext.Provider
      value={value}
    >
      <IdleTimerProvider
        timeout={idlenessTimeout}
        onIdle={logoutHandler}
      >
        {children}
      </IdleTimerProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
